/* One line of the book's history: what happened, the detail under it, and the
 * amount. Shared by the History screen and the recent list on Value, so the two
 * cannot come to describe the same record differently. See `state/activity.ts`
 * for where the entries come from. */

import {
  moneyExact, moneyExactSigned, pct, price as fmtPrice, shares as fmtShares, sharesOf,
} from '../lib/format';
import { amountTone, type ActivityItem } from '../state/activity';

const KNOWN = new Set(['USD', 'PLN', 'EUR', 'HKD']);

/** Money in the currency it was recorded in. USDT and anything else the
    formatter has no symbol for is written out after the figure. */
function cash(v: number, currency: string, signed = false): string {
  const format = signed ? moneyExactSigned : moneyExact;
  return KNOWN.has(currency)
    ? format(v, currency as 'USD')
    : `${format(v, 'USD').replace('$', '')} ${currency}`;
}

function unitPrice(v: number, currency: string): string {
  return KNOWN.has(currency)
    ? fmtPrice(v, currency as 'USD')
    : `${fmtPrice(v, 'USD').replace('$', '')} ${currency}`;
}

interface Described {
  glyph: string;
  title: string;
  sub: string;
  amount?: string | undefined;
  detail?: string | undefined;
  detailTone?: 'up' | 'down' | 'dim' | undefined;
}

export function ActivityRow({ item, nameOf, open, onOpen }: {
  item: ActivityItem;
  nameOf: (id: string) => string;
  open: boolean;
  onOpen: () => void;
}) {
  const d = describe(item, nameOf);
  return (
    <button type="button" className="activity-row" onClick={onOpen}
            aria-expanded={item.source.type === 'trade' ? open : undefined}>
      <span className="activity-glyph" aria-hidden="true">{d.glyph}</span>
      <span className="activity-main">
        <span className="activity-title">{d.title}</span>
        <span className="activity-sub">{d.sub}</span>
      </span>
      <span className="activity-amt">
        {d.amount ? <span className={`num ${amountTone(item) ?? ''}`} style={{ fontWeight: 600 }}>{d.amount}</span> : null}
        {d.detail ? <span className={`num ${d.detailTone ?? 'dim'}`} style={{ fontSize: 11.5 }}>{d.detail}</span> : null}
      </span>
    </button>
  );
}

function describe(item: ActivityItem, nameOf: (id: string) => string): Described {
  const account = item.account ? nameOf(item.account) : null;
  const join = (...parts: Array<string | null | undefined | false>) => parts.filter(Boolean).join(' · ');
  const ccy = item.currency;
  const result = (v: number) => (v >= 0 ? 'up' : 'down');

  switch (item.kind) {
    case 'buy':
    case 'sell': {
      const sold = item.kind === 'sell';
      const gain = item.realizedGain;
      return {
        glyph: sold ? '−' : '+',
        title: `${sold ? 'Sold' : 'Bought'} ${item.ticker}`,
        sub: join(
          `${fmtShares(item.shares ?? 0)} @ ${unitPrice(item.price ?? 0, ccy)}`,
          account,
          item.fee ? `fee ${cash(item.fee, ccy)}` : null,
        ),
        amount: cash(item.amount ?? 0, ccy, true),
        /* On a sale, the gain or a plain statement that none was recorded —
           never a zero, which would claim the sale broke even. */
        detail: !sold ? undefined : typeof gain === 'number' ? `${cash(gain, ccy, true)} realised` : 'no gain recorded',
        detailTone: sold && typeof gain === 'number' ? result(gain) : 'dim',
      };
    }
    case 'dividend':
      return {
        glyph: '%',
        title: `Dividend ${item.ticker}`,
        sub: join(
          `${fmtShares(item.shares ?? 0)} × ${unitPrice(item.price ?? 0, ccy)}`,
          account,
          item.tax ? `tax ${cash(item.tax, ccy)}` : null,
        ),
        amount: cash(item.amount ?? 0, ccy, true),
      };
    case 'deposit':
    case 'withdrawal':
      return {
        glyph: item.kind === 'deposit' ? '↓' : '↑',
        title: item.kind === 'deposit' ? 'Deposit' : 'Withdrawal',
        sub: join(account ?? 'across the book', item.note),
        amount: cash(item.amount ?? 0, ccy, true),
      };
    case 'conversion': {
      /* The same record carries a currency change and a move between accounts.
         In one currency it is only the move, and "USD → USD" said nothing about
         where the money went. */
      const moved = item.toAccount !== undefined && item.toAccount !== item.account;
      if (moved && item.toCurrency === ccy) {
        return {
          glyph: '⇄',
          title: `Transfer to ${nameOf(item.toAccount!)}`,
          sub: join(account ? `from ${account}` : null, item.note),
          amount: cash(item.amount ?? 0, ccy, true),
          detail: cash(item.toAmount ?? 0, item.toCurrency ?? ccy, true),
        };
      }
      return {
        glyph: '⇄',
        title: `${ccy} → ${item.toCurrency}`,
        sub: join(
          account,
          moved ? `into ${nameOf(item.toAccount!)}` : null,
          item.note,
        ),
        amount: cash(item.amount ?? 0, ccy, true),
        detail: cash(item.toAmount ?? 0, item.toCurrency ?? ccy, true),
      };
    }
    case 'transfer':
      return {
        glyph: '→',
        title: `Moved ${item.ticker}`,
        sub: join(`${account} → ${item.toAccount ? nameOf(item.toAccount) : ''}`, item.note),
        amount: sharesOf(item.shares ?? 0, item.ticker ?? ''),
      };
    case 'contract_open':
      return {
        glyph: '◇',
        title: `Opened ${item.ticker} ${item.side} ${item.leverage}×`,
        sub: join(`entry ${unitPrice(item.price ?? 0, 'USDT')}`, `margin ${cash(item.amount ?? 0, 'USDT')}`),
      };
    case 'contract_close':
      return {
        glyph: '◆',
        title: `${item.final ? 'Closed' : 'Part-closed'} ${item.ticker}`,
        sub: typeof item.portion === 'number'
          ? `${pct(item.portion * 100, 0)} of what was open`
          : 'the result as typed in on closing',
        amount: cash(item.amount ?? 0, 'USDT', true),
      };
  }
}
